import { Form, InputNumber, Modal, Select } from '@maxtropy/components';
import { Space, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { useMemo } from 'react';
import { ValveDevice, OPERATOR_OPTIONS, ConditionOperator } from '../types';
import { getDeviceProperty } from '@/api/scada';
import { DataPropertyType } from '@/shared/types';
import {
  apiV2AreaGetAllMediumValveDetailPost,
  apiV2AreaUpsertValveStateConfigPost,
} from '@maxtropy/device-customer-apis-v2';
import type { Rule } from 'rc-field-form/lib/interface';

interface StatusConfigModalProps {
  visible: boolean;
  device?: ValveDevice;
  areaId?: number;
  mediumId?: number;
  onCancel: () => void;
}

// 判断两个条件区间是否有交集
const hasRangeIntersection = (
  openOperator: ConditionOperator,
  openValue: number,
  closeOperator: ConditionOperator,
  closeValue: number
): boolean => {
  // 将条件转换为区间表示 [min, max, leftInclusive, rightInclusive]
  // open 表示满足条件时阀门开，close 表示满足条件时阀门关
  const getRange = (operator: ConditionOperator, value: number) => {
    switch (operator) {
      case ConditionOperator.GreaterThan:
        return { min: value, max: Infinity, minInclusive: false, maxInclusive: false };
      case ConditionOperator.GreaterThanOrEqual:
        return { min: value, max: Infinity, minInclusive: true, maxInclusive: false };
      case ConditionOperator.LessThan:
        return { min: -Infinity, max: value, minInclusive: false, maxInclusive: false };
      case ConditionOperator.LessThanOrEqual:
        return { min: -Infinity, max: value, minInclusive: false, maxInclusive: true };
      default:
        return null;
    }
  };

  const openRange = getRange(openOperator, openValue);
  const closeRange = getRange(closeOperator, closeValue);

  if (!openRange || !closeRange) return false;

  // 两个区间有交集的条件：
  // max(min1, min2) <= min(max1, max2) 且边界条件满足
  const overlapMin = Math.max(openRange.min, closeRange.min);
  const overlapMax = Math.min(openRange.max, closeRange.max);

  if (overlapMin > overlapMax) return false;
  if (overlapMin === overlapMax) {
    const isPointIncludedByRange = (
      range: { min: number; max: number; minInclusive: boolean; maxInclusive: boolean },
      point: number
    ) => {
      const greaterThanMin = point > range.min || (point === range.min && range.minInclusive);
      const lessThanMax = point < range.max || (point === range.max && range.maxInclusive);

      return greaterThanMin && lessThanMax;
    };

    return isPointIncludedByRange(openRange, overlapMin) && isPointIncludedByRange(closeRange, overlapMin);
  }

  // overlapMin < overlapMax 的情况
  // 需要检查边界的包含性
  if (overlapMin === openRange.min && overlapMin === closeRange.min) {
    if (!openRange.minInclusive && !closeRange.minInclusive) {
      // 两边都不包含这个最小值，但区间仍然有交集（比最小值大的部分）
    }
  }
  if (overlapMax === openRange.max && overlapMax === closeRange.max) {
    if (!openRange.maxInclusive && !closeRange.maxInclusive) {
      // 两边都不包含这个最大值，但区间仍然有交集（比最大值小的部分）
    }
  }

  return true;
};

const StatusConfigModal: React.FC<StatusConfigModalProps> = ({ visible, device, onCancel, areaId, mediumId }) => {
  const [form] = Form.useForm();
  // 监听数据属性选择
  const selectedPropertyId = Form.useWatch('dataPropertyId', form);

  // 获取设备数据属性
  const { data: propertyList } = useRequest(() => getDeviceProperty(device?.deviceId!), {
    ready: visible && !!device?.deviceId,
    refreshDeps: [visible, device?.deviceId],
  });

  const selectedProperty = useMemo(() => {
    return propertyList?.find(p => p.dataPropertyId === selectedPropertyId);
  }, [propertyList, selectedPropertyId]);

  const isEnumType = selectedProperty?.dataPropertyType === DataPropertyType.ENUM;
  const isYcType = selectedProperty?.dataPropertyType === DataPropertyType.YC;

  const revalidateValueFields = () => {
    form
      .validateFields([
        ['valveOpenDetail', 'value'],
        ['valveCloseDetail', 'value'],
      ])
      .catch(() => undefined);
  };

  const validateEnumValue = (_: unknown, value: number | undefined) => {
    const openValue = form.getFieldValue(['valveOpenDetail', 'value']);
    const closeValue = form.getFieldValue(['valveCloseDetail', 'value']);
    const hasCurrentValue = value !== undefined && value !== null;
    const hasBothValues =
      openValue !== undefined && openValue !== null && closeValue !== undefined && closeValue !== null;

    if (hasCurrentValue && hasBothValues && openValue === closeValue) {
      return Promise.reject(new Error('阀门开和阀门关不能选择同一个枚举值'));
    }

    return Promise.resolve();
  };

  const validateYcValue = (_: unknown, value: number | undefined) => {
    const openOperator = form.getFieldValue(['valveOpenDetail', 'compareType']) as ConditionOperator | undefined;
    const closeOperator = form.getFieldValue(['valveCloseDetail', 'compareType']) as ConditionOperator | undefined;
    const openValue = form.getFieldValue(['valveOpenDetail', 'value']);
    const closeValue = form.getFieldValue(['valveCloseDetail', 'value']);
    const hasCurrentValue = value !== undefined && value !== null;
    const hasBothValues =
      openValue !== undefined && openValue !== null && closeValue !== undefined && closeValue !== null;

    if (
      hasCurrentValue &&
      openOperator &&
      closeOperator &&
      hasBothValues &&
      hasRangeIntersection(openOperator, Number(openValue), closeOperator, Number(closeValue))
    ) {
      return Promise.reject(new Error('阀门开和阀门关的条件区间不能有交集'));
    }

    return Promise.resolve();
  };

  const createValueRules = (type: 'enum' | 'yc'): Rule[] => {
    if (type === 'enum') {
      return [{ required: true, message: '请选择' }, { validator: validateEnumValue }];
    }

    return [
      { required: true, message: '请输入' },
      { type: 'number', message: '请输入数值' },
      { validator: validateYcValue },
    ];
  };

  // 数据属性选项
  const propertyOptions = useMemo(() => {
    return propertyList?.map(p => ({
      value: p.dataPropertyId,
      label: p.dataPropertyName,
      type: p.dataPropertyType,
    }));
  }, [propertyList]);

  // 枚举值选项
  const enumValueOptions = useMemo(() => {
    if (!selectedProperty?.enumValue) return [];
    return selectedProperty.enumValue.map((e: any) => ({
      value: e.value,
      label: e.name,
    }));
  }, [selectedProperty]);

  const { loading } = useRequest(
    () => apiV2AreaGetAllMediumValveDetailPost({ id: areaId, mediumId, deviceId: device!.deviceId }),
    {
      onSuccess: data => {
        form.setFieldsValue({ ...data.valveStatusDetail });
      },
      ready: visible && !!device?.deviceId,
      refreshDeps: [visible, device?.deviceId, mediumId, areaId],
    }
  );

  // 数据属性变化时清空相关字段
  const handlePropertyChange = () => {
    form.setFieldsValue({
      openValue: undefined,
      closeValue: undefined,
      openCondition: undefined,
      closeCondition: undefined,
    });
  };

  // 确认
  const handleOk = () => {
    form.validateFields().then(values => {
      const property = propertyList!.find(p => p.dataPropertyId === values.dataPropertyId);

      apiV2AreaUpsertValveStateConfigPost({
        id: areaId,
        mediumId,
        deviceId: device!.deviceId,
        valveStatusDetail: {
          dataPropertyName: property!.dataPropertyName,
          type: property!.dataPropertyType,
          ...values,
        },
      }).then(() => {
        onCancel();
      });
    });
  };

  // 取消
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="配置"
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      maskClosable={false}
      contentClassName="modal-form-content"
    >
      <Spin spinning={loading}>
        <Form form={form}>
          <Form.Item name="dataPropertyId" label="数据属性" rules={[{ required: true, message: '请选择数据属性' }]}>
            <Select
              placeholder="请选择数据属性"
              options={propertyOptions}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              onChange={handlePropertyChange}
            />
          </Form.Item>
          {/* 枚举类型 */}
          {isEnumType && (
            <>
              <Form.Item name={['valveOpenDetail', 'value']} label="阀门开" rules={createValueRules('enum')}>
                <Select placeholder="请选择" options={enumValueOptions} onChange={revalidateValueFields} />
              </Form.Item>

              <Form.Item name={['valveCloseDetail', 'value']} label="阀门关" rules={createValueRules('enum')}>
                <Select placeholder="请选择" options={enumValueOptions} onChange={revalidateValueFields} />
              </Form.Item>
            </>
          )}

          {/* 遥测值类型 */}
          {isYcType && (
            <>
              <Form.Item label="阀门开" required>
                <Space style={{ width: '100%' }} size={8}>
                  <Form.Item
                    name={['valveOpenDetail', 'compareType']}
                    noStyle
                    rules={[{ required: true, message: '请选择' }]}
                  >
                    <Select
                      options={OPERATOR_OPTIONS}
                      placeholder="请选择"
                      style={{ width: 120 }}
                      onChange={revalidateValueFields}
                    />
                  </Form.Item>
                  <Form.Item name={['valveOpenDetail', 'value']} noStyle rules={createValueRules('yc')}>
                    <InputNumber placeholder="请输入" style={{ width: 232 }} onChange={revalidateValueFields} />
                  </Form.Item>
                </Space>
              </Form.Item>
              <Form.Item label="阀门关" required>
                <Space style={{ width: '100%' }} size={8}>
                  <Form.Item
                    name={['valveCloseDetail', 'compareType']}
                    noStyle
                    rules={[{ required: true, message: '请选择' }]}
                  >
                    <Select
                      options={OPERATOR_OPTIONS}
                      placeholder="请选择"
                      style={{ width: 120 }}
                      onChange={revalidateValueFields}
                    />
                  </Form.Item>
                  <Form.Item name={['valveCloseDetail', 'value']} noStyle rules={createValueRules('yc')}>
                    <InputNumber placeholder="请输入" style={{ width: 232 }} onChange={revalidateValueFields} />
                  </Form.Item>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default StatusConfigModal;
